import React, { useEffect, useState } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { CheckCircle, Star } from '@mui/icons-material'
import { Button, CircularProgress } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { AnimatePresence, motion } from 'framer-motion'
import { colors } from '@global/constants/colors'
import { addToFavorites, removeFromFavorites } from 'pages/datasets/api'
import { useDatasetsContext } from 'pages/datasets/context'

enum FavoriteStatus {
  UPDATING = 'UPDATING',
  SUCCESS = 'SUCCESS',
  DEFAULT = 'DEFAULT',
}

const StyledButton = styled(Button)`
  min-width: auto;
  padding: 0;
  margin-left: 10px;
  border-radius: 100%;
`

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  top: -1px;
  padding: 3px;
`

const progressIcon = css`
  position: absolute;
  display: flex;
`

const checkIcon = css`
  color: ${colors.green.main};
  position: absolute;
  left: 12px;
  bottom: 8px;
  width: 0.7em;
  z-index: 1;
`

const useStyles = makeStyles(() => ({
  iconButton: {
    height: 22,
    width: 22,
    top: -1,
  },
  favoriteStar: {
    color: colors.chineseYellow,
    '&:hover': {
      color: 'rgba(0, 0, 0, 0.5)',
    },
  },
  star: {
    color: 'rgba(0, 0, 0, 0.26)',
    '&:hover': {
      color: 'rgba(0, 0, 0, 0.5)',
    },
  },
}))

const MotionSpan = ({ children, ...props }) => (
  <motion.span initial={{ opacity: 1 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} {...props}>
    {children}
  </motion.span>
)

interface Props {
  id: number
  favorite: boolean
}

/**
 * Limit rerendering to only prop changes. This was not happening.
 * @returns
 */
function compare(prev, next) {
  return prev.id === next.id || prev.favorite === next.favorite
}

export const Favorite = React.memo(({ id, favorite }: Props) => {
  const [starred, setStarred] = useState<boolean>(favorite)
  const [status, setStatus] = useState<FavoriteStatus>(FavoriteStatus.DEFAULT)

  const { handleToggleFavorite } = useDatasetsContext()

  const isUpdating = status === FavoriteStatus.UPDATING
  const isSuccess = status === FavoriteStatus.SUCCESS
  const iconOpacity = isUpdating ? 0.5 : 1

  const classes = useStyles()

  useEffect(() => {
    setStarred(favorite)
  }, [favorite])

  return (
    <StyledButton
      onClick={e => {
        handleClickFavorite(e, id)
      }}
    >
      <IconContainer>
        <AnimatePresence>
          {isUpdating && (
            <MotionSpan key={status} css={progressIcon}>
              <CircularProgress size={15} />
            </MotionSpan>
          )}
          {isSuccess && (
            <MotionSpan key={status}>
              <CheckCircle css={checkIcon} />
            </MotionSpan>
          )}
        </AnimatePresence>
        <Star
          className={starred ? classes.favoriteStar : classes.star}
          sx={{ opacity: iconOpacity, height: 17, width: 17 }}
        />
      </IconContainer>
    </StyledButton>
  )

  function handleClickFavorite(e: any, datasetId: number) {
    e.stopPropagation()
    setStatus(FavoriteStatus.UPDATING)

    const updater = starred ? removeFromFavorites : addToFavorites

    updater(datasetId)
      .then(() => {
        setStatus(FavoriteStatus.SUCCESS)
        setStarred(prev => !prev)
        setTimeout(() => {
          setStatus(FavoriteStatus.DEFAULT)
          handleToggleFavorite(datasetId, !starred)
        }, 1000)
      })
      .catch((err: unknown) => {
        console.error('An error has occurred while adding or removing favorites', err)
      })
  }
}, compare)

Favorite.displayName = 'Favorite'
